
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase, type News } from '../../lib/supabase';
import VideoPlayer from '../../components/VideoPlayer';
import TabBar from '../home/components/TabBar';

const NewsPage = () => {
  const navigate = useNavigate();
  const [news, setNews] = useState<News[]>([]);
  const [filteredNews, setFilteredNews] = useState<News[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  useEffect(() => {
    fetchNews();
    
    // Escuchar actualizaciones
    const handleNewsUpdate = () => {
      fetchNews();
    };
    
    window.addEventListener('newsDataUpdated', handleNewsUpdate);
    
    return () => {
      window.removeEventListener('newsDataUpdated', handleNewsUpdate);
    };
  }, []);

  useEffect(() => {
    filterNews();
  }, [news, selectedCategory]);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('news')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error fetching news:', error);
        setNews([]);
        return;
      }

      setNews(data || []);
    } catch (error) {
      console.error('Error:', error);
      setNews([]);
    } finally {
      setLoading(false);
    }
  };

  const filterNews = () => {
    if (selectedCategory === 'all') {
      setFilteredNews(news);
    } else {
      setFilteredNews(news.filter(item => item.category === selectedCategory));
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { 
      day: 'numeric', 
      month: 'long',
      year: 'numeric'
    });
  };

  const handleNewsClick = (newsItem: News) => {
    navigate(`/news/${newsItem.id}`);
  };

  const categories = ['all', 'Partidos', 'Club', 'Eventos', 'Otros'];
  const categoryLabels: Record<string, string> = {
    all: 'Todas',
    Partidos: 'Partidos',
    Club: 'Club',
    Eventos: 'Eventos',
    Otros: 'Otros'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black pb-20">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-black/80 backdrop-blur-md border-b border-white/10">
        <div className="px-4 py-4 flex items-center gap-3">
          <button
            onClick={() => navigate('/')}
            className="w-10 h-10 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 transition-colors"
          >
            <i className="ri-arrow-left-line text-white text-xl"></i>
          </button>
          <h1 className="text-lg font-bold text-white">Noticias</h1>
        </div>

        {/* Categories Filter */}
        <div className="px-4 pb-4">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setSelectedCategory(category)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  selectedCategory === category
                    ? 'bg-pink-500 text-white'
                    : 'bg-white/10 text-white/70 hover:bg-white/20'
                }`}
              >
                {categoryLabels[category]}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block w-8 h-8 border-3 border-white/20 border-t-pink-500 rounded-full animate-spin"></div>
            <p className="mt-4 text-white/60">Cargando noticias...</p>
          </div>
        ) : filteredNews.length === 0 ? (
          <div className="text-center py-12 bg-white/5 backdrop-blur-sm rounded-2xl border border-white/10">
            <i className="ri-newspaper-line text-5xl text-white/30 mb-3"></i>
            <p className="text-white/60">
              {selectedCategory === 'all' 
                ? 'No hay noticias disponibles' 
                : `No hay noticias en la categoría "${categoryLabels[selectedCategory]}"`
              }
            </p>
          </div>
        ) : (
          <div className="space-y-4 max-w-2xl mx-auto">
            {filteredNews.map((item) => (
              <div
                key={item.id}
                onClick={() => handleNewsClick(item)}
                className="bg-white/10 backdrop-blur-sm rounded-2xl border border-white/20 overflow-hidden hover:bg-white/15 transition-all duration-300 hover:scale-[1.02] cursor-pointer"
              >
                {(item.image || item.video_url) && (
                  <div className="w-full bg-white/5">
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.title}
                        className="w-full h-48 object-cover"
                        onError={(e) => {
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                        }}
                      />
                    ) : item.video_url ? (
                      <VideoPlayer url={item.video_url} className="rounded-none" />
                    ) : null}
                  </div>
                )}

                <div className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-bold text-white text-lg line-clamp-2 flex-1">
                      {item.title}
                    </h3>
                  </div>
                  {item.video_url && (
                    <div className="flex items-center gap-2 text-sm text-pink-300 mb-2">
                      <i className="ri-play-circle-line"></i>
                      <span>Video disponible</span>
                    </div>
                  )}
                  <p className="text-sm text-white/70 mb-3 line-clamp-3">
                    {item.text}
                  </p>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-xs text-white/50">
                      <div className="flex items-center gap-1">
                        <i className="ri-user-line"></i>
                        <span>{item.author || 'Admin'}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                    </div>
                    <i className="ri-arrow-right-line text-pink-400 text-xl"></i>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <TabBar />
    </div>
  );
};

export default NewsPage;
